/* eslint-disable react/jsx-no-constructed-context-values */
import type { AccountDBSource } from '@partful/shared-types';
import { ConfirmationProvider } from '@partful/shared/ui/Confirmation/ConfirmationProvider';
import { SnackBarProvider } from '@partful/shared/ui/SnackBar/SnackBarProvider';
import { ShoppingCartPanelProvider } from '@providers/ShoppingCart';
import { TenantContext } from '@providers/Tenant';
import { ThemeProvider } from '@providers/Theme';
import { UserProvider } from '@providers/User';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { render, renderHook, RenderOptions } from '@testing-library/react';
import { History } from 'history';
import React, { PropsWithChildren, ReactElement } from 'react';
import { I18nextProvider } from 'react-i18next';
import { MemoryRouter, Router } from 'react-router-dom';
import i18n from 'src/i18n';

const getNewQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
      },
    },
  });

export const queryClient = getNewQueryClient();

interface RouterProviderProps {
  initialEntries?: History.LocationDescriptor<unknown>[] | undefined;
  history?: History;
}
interface RenderWithRouterOptions {
  initialEntries?: History.LocationDescriptor<unknown>[] | undefined;
  children: React.ReactNode;
  tenantContext?: AccountDBSource;
  history?: History;
  noRouter?: boolean;
}

function RouterProvider({ history, initialEntries, children }: PropsWithChildren<RouterProviderProps>) {
  return history ? (
    <Router history={history}>{children}</Router>
  ) : (
    <MemoryRouter initialEntries={initialEntries}>{children}</MemoryRouter>
  );
}

function OptionalRouterProvider({
  noRouter,
  history,
  initialEntries,
  children,
}: PropsWithChildren<RouterProviderProps & { noRouter: boolean }>) {
  return noRouter ? (
    <>{children}</>
  ) : (
    <RouterProvider history={history} initialEntries={initialEntries}>
      {children}
    </RouterProvider>
  );
}

export function AllTheProviders({
  children,
  initialEntries,
  tenantContext,
  history,
  noRouter = false,
}: RenderWithRouterOptions) {
  const queryClientToUse = getNewQueryClient();

  return (
    <QueryClientProvider client={queryClientToUse}>
      <TenantContext.Provider
        value={tenantContext || ({ enabledFeatures: ['shopping-cart', 'show-non-buyable-parts'] as any } as any)}
      >
        <UserProvider>
          <ShoppingCartPanelProvider>
            <ThemeProvider>
              <I18nextProvider i18n={i18n}>
                <OptionalRouterProvider noRouter={noRouter} history={history} initialEntries={initialEntries}>
                  <SnackBarProvider>
                    <ConfirmationProvider>{children}</ConfirmationProvider>
                  </SnackBarProvider>
                </OptionalRouterProvider>
              </I18nextProvider>
            </ThemeProvider>
          </ShoppingCartPanelProvider>
        </UserProvider>
      </TenantContext.Provider>
    </QueryClientProvider>
  );
}

export type CustomRenderOptions = Omit<RenderOptions, 'wrapper'> & {
  wrapperProps?: Omit<RenderWithRouterOptions, 'children'>;
};

const customRender = (ui: ReactElement, options?: CustomRenderOptions) => {
  const { wrapperProps, ...restOfOptions } = options ?? {};

  return render(ui, {
    wrapper: (props) => <AllTheProviders {...props} {...options?.wrapperProps} />,
    ...restOfOptions,
  });
};

export { customRender as render };

/**
 * USE WITH CAUTION!
 * This will consume the console.error log so it won't appear in the terminal of the test case.
 *
 * THIS SHOULD ONLY BE USED when a test is EXPECTED to have console log. For example, your test will cause the browser to throw an exception which is EXPECTED
 * DO NOT USE THIS to silence React warnings like "Warning: Can't perform a React state update on an unmounted component." - you should fix this in your test instead!
 */
export const ignoreConsoleError = () => {
  // eslint-disable-next-line no-console
  console.error = jest.fn();
};

export const ignoreNavigationConsoleError = () => {
  // eslint-disable-next-line no-console
  const originalConsoleError = console.error;
  // eslint-disable-next-line no-console
  console.error = (...args) => {
    if (args[0]?.message?.startsWith('Not implemented: navigation')) {
      return;
    }
    originalConsoleError(...args);
  };
};

export const renderQueryHook = <TResult,>(
  callback: () => TResult,
  options?: { wrapper?: React.ComponentType<any> }
) => {
  // most of the time we don't want retries in tests because we're in control of the requests
  // so we don't expect the test to pass/fail because of retries
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: { retry: false },
      mutations: { retry: false },
    },
  });

  const wrapper = ({ children }: { children: React.ReactNode }) => (
    <QueryClientProvider client={queryClient}>
      {options?.wrapper ? <options.wrapper>{children}</options.wrapper> : children}
    </QueryClientProvider>
  );

  return {
    ...renderHook(callback, { wrapper }),
    queryClient,
  };
};
